import * as React from "react";
import { Button, PopupPositionsType } from "react-ui";
import { Input as BaseInput } from "react-ui";
import { useDebounce } from "use-debounce";
import { findDOMNode } from "react-dom";

import { PicklistDataItem } from "../../../../Engine/Core/Picklists/PicklistTypes";
import { MetricsValueSenderActions } from "../../../Internals/Metrics/MetricsValueSender";
import { FocusManagementObserver } from "../../../Services/FocusManagement/FocusManagementObserver";
import { AbsoluteResolvedModelPath } from "../../../../../../Common/ModelPath/ModelPath";
import { ControlValidationProps } from "../../../Validations/ValidationProps";
import { ControlTooltipProps } from "../../../Internals/Tooltip/ControlTooltipProps";
import { Text } from "../../../ValueViewers/Text/Text";
import { InternalIcon } from "../../../Icons/Icon";
import { useMetrics } from "../../../Internals/Metrics/MetricsProviderContext";
import {
    TREE_PICKLIST_CLOSE_MODAL,
    TREE_PICKLIST_OPEN_MODAL,
    TREE_PICKLIST_TYPE_IN_INPUT,
} from "../../../metricsAction";
import { emptyRef } from "../../../TypingUtils";
import { arrayHasAtLeastOneItem, isNotNullOrUndefined } from "../../../../../../Common/TypingUtils";
import { Tooltip } from "../../../Internals/Tooltip/Tooltip";
import { TextOverflowHint } from "../../../Internals/TextOverflowHint/TextOverflowHint";
import { getCandyHub } from "../../../../Forms/Sentry/InitializeSentry";
import { GroupButtonContainer, Root } from "../Common/Styles/Picklist.styles";
import { FilterBuilder } from "../Common/FilterBuilder";
import { PicklistControlDependency } from "../Common/PicklistItemProvider";
import { useAsyncEffect } from "../../../../../../Common/UseAsyncEffect";
import { useScrollTo } from "../../../Internals/Contexts/ScrollToContext";
import { toHtmlElementOrNull } from "../../../Commons/DOM/DomHelpers";
import { changeRelatedFields } from "../Common/changeRelatedFields";

import { TreePicklistModal, TreeQueryResponse } from "./TreePicklistModal/TreePicklistModal";
import { TreePicklistHint } from "./TreePicklistHint/TreePicklistHint";
import {
    BaseTreePicklistSearchProps,
    FilterQueryOptions,
    getTreePickListItemsByValue,
    getTreePickListItemsBySearchQuery,
    TreeQuery,
} from "./TreePicklistItemProvider";
import { VisibleChildrenCountProvider } from "./TreePicklistNode/VisibleElementsCountProvider";
import { TreePicklistInput } from "./TreePicklistInput";

export interface TreePicklistProps extends ControlValidationProps, ControlTooltipProps, BaseTreePicklistSearchProps {
    title: string;

    parentColumnName: string;
    childColumnName: string;
    display: string[];
    onChange: (items?: PicklistDataItem[] | PicklistDataItem, fieldName?: string) => Promise<void>;
    minPickLevel: number;
    allowedToPickLevels?: number[];
    fieldToDisableElementPick?: string;

    dependencies: PicklistControlDependency[];
    bindingPath: AbsoluteResolvedModelPath;
    value: string;
    valueName?: string;
    description?: string;
    gId?: string;
    headers?: string[];
    isTooltipEnabled: boolean;
    tooltipPosition?: PopupPositionsType;

    mapChangesField: string[];
    setDependence: (x1: string, x2: string) => void;

    width?: string | number;
    multiple?: boolean;
    metrics: MetricsValueSenderActions;
    setPicklistDataItem?: (values: undefined | PicklistDataItem | Array<undefined | PicklistDataItem>) => void;
    isReadOnly?: boolean;
    onClick?: () => void;
    "data-tid"?: string;
    descriptionInModal: string | React.ReactNode;
    hintFields?: string[];
    hintWidth?: number;
    overwriteOnInit?: boolean;
}

export const TreePicklist = (props: TreePicklistProps) => {
    if (props.isReadOnly) {
        return (
            <Text
                value={props.value}
                data-tid={props["data-tid"]}
                bindingPath={props.bindingPath}
                onClick={props.onClick}
            />
        );
    }
    return <InteractiveTreePicklist {...props} />;
};

const InteractiveTreePicklist = (props: TreePicklistProps) => {
    const [modalOpened, setModalOpened] = React.useState(false);
    const track = useMetrics();
    const input = React.useRef<BaseInput>(emptyRef);
    const [inputValue, setInputValue] = React.useState(props.value);
    const [denouncedInputValue] = useDebounce(inputValue, 500);
    const scrollTo = useScrollTo();

    React.useEffect(() => {
        setInputValue(props.value);
    }, [props.value]);

    const handleOpenModal = () => {
        setModalOpened(true);
        track(TREE_PICKLIST_OPEN_MODAL, { gid: props.gId });
    };

    const handleCloseModal = () => {
        setModalOpened(false);
        track(TREE_PICKLIST_CLOSE_MODAL, { gid: props.gId });
    };

    const renderModal = () => (
        <VisibleChildrenCountProvider>
            <TreePicklistModal
                onClose={handleCloseModal}
                onSave={handleSaveModal}
                {...props}
                treeQuery={itemSearchQuery}
            />
        </VisibleChildrenCountProvider>
    );

    const onChangeInputValue = async (value: string): Promise<void> => {
        if (value === "") {
            await onChangeValue(value, { items: [], suitableCount: 0 });
        } else {
            const filterBuilder = new FilterBuilder();
            const filterQueryOptions: FilterQueryOptions = { searchQuery: value, nodeFilter: filterBuilder };
            await inputChangeQuery(filterQueryOptions)
                .then(picklistQueryResponse => onChangeValue(value, picklistQueryResponse))
                .catch(e => getCandyHub().captureException(e));
        }
    };

    const onChangeValue = async (value: string, queryResponse: TreeQueryResponse): Promise<void> => {
        if (props.multiple) {
            let instances: string[];
            if (value) {
                instances = value
                    .split(",")
                    .map(val => val.trim())
                    .filter(val => val);
            } else {
                instances = [""];
            }

            const itemsByValues = instances.map(instance => {
                const itemByValue = queryResponse.items.find(
                    item => item[props.searchByField]?.toString() === instance
                );
                return itemByValue ?? { [props.searchByField]: instance };
            });
            props.setPicklistDataItem?.(itemsByValues);
            await props.onChange(itemsByValues.filter(isNotNullOrUndefined));
        } else {
            const itemByValue = queryResponse.items.find(item => item[props.searchByField]?.toString() === value);
            await props.onChange(itemByValue ?? { [props.searchByField]: value }, props.searchByField);
            props.setPicklistDataItem?.(itemByValue);
        }
        if (props.overwriteOnInit) {
            changeRelatedFields(value, queryResponse.items, props);
        }
    };

    const inputChangeQuery = async (filterQueryOptions: FilterQueryOptions): Promise<TreeQueryResponse> => {
        const response = await getTreePickListItemsBySearchQuery(filterQueryOptions, props);

        return response;
    };

    const itemSearchQuery: TreeQuery = async (filterQueryOptions: FilterQueryOptions): Promise<TreeQueryResponse> => {
        const response = await getTreePickListItemsByValue(filterQueryOptions, props);

        return response;
    };

    const handleSaveModal = async (items: PicklistDataItem[]) => {
        if (props.multiple) {
            await handleSaveMultipleNodesModal(items);
        } else {
            await handleSaveSingleNodeModal(items);
        }
    };

    const handleSaveMultipleNodesModal = async (items: PicklistDataItem[]) => {
        const valueToMetrics = prepareMetricsFromMultipleNodes(items);
        sendMetricsIfCallbackExists(valueToMetrics);

        await triggerMultipleNodesOnChangeCallback(items);

        setPicklistDataAndCloseModal(items);
    };

    const prepareMetricsFromMultipleNodes = (items: PicklistDataItem[]): string =>
        items.map(item => item[props.searchByField]).join(", ");

    const triggerMultipleNodesOnChangeCallback = async (items: PicklistDataItem[]): Promise<void> => {
        await props.onChange(items);
    };

    const handleSaveSingleNodeModal = async (items: PicklistDataItem[]) => {
        if (!arrayHasAtLeastOneItem(items)) {
            return;
        }

        const [item] = items;
        const valueToMetrics = await prepareMetricsFromSingleNode(item);
        sendMetricsIfCallbackExists(valueToMetrics);

        await triggerSingleNodeOnChangeCallback(item);

        setPicklistDataAndCloseModal(item);
    };

    const prepareMetricsFromSingleNode = async (item: PicklistDataItem): Promise<string> => {
        let valueToMetrics = "";
        props.dependencies.forEach(dependence => {
            if (dependence.path.toLegacyPath() === props.bindingPath.toLegacyPath()) {
                valueToMetrics = item[props.parentColumnName]?.toString() ?? "";
            }
        });

        return valueToMetrics;
    };

    const triggerSingleNodeOnChangeCallback = async (item: PicklistDataItem): Promise<void> => {
        for (const fieldName of props.mapChangesField) {
            await props.onChange(item, fieldName);
        }
    };

    const sendMetricsIfCallbackExists = (valueToMetrics: string) => {
        if (props.metrics) {
            props.metrics.sendValue(props.bindingPath, valueToMetrics, 0, 0, undefined, props.gId, "TreePicklist");
        }
    };

    const setPicklistDataAndCloseModal = (items: PicklistDataItem | PicklistDataItem[]) => {
        props.setPicklistDataItem?.(items);
        setModalOpened(false);
    };

    const renderInput = () => {
        const { error, warning, description, isTooltipEnabled, value } = props;
        const errorText = error || warning;
        const hasErrorOrWarning = !!errorText;
        const tooltipTrigger = hasErrorOrWarning || isTooltipEnabled ? "hover&focus" : "closed";
        const tooltipPosition = props.tooltipPosition;

        // BUG [FS-9231](https://yt.skbkontur.ru/issue/FS-9231)
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useAsyncEffect(async () => {
            track(TREE_PICKLIST_TYPE_IN_INPUT, {
                value: denouncedInputValue,
                gid: props.gId,
            });
            await onChangeInputValue(denouncedInputValue);
        }, [denouncedInputValue]);

        const renderRawInput = () => {
            const hintToShow = props.multiple ? undefined : props.valueName;

            return (
                <TreePicklistInput
                    onValueChange={nextValue => setInputValue(nextValue)}
                    value={inputValue}
                    hint={hintToShow}
                    width={props.width}
                    ref={input}
                    error={!!error}
                    warning={!!warning}
                />
            );
        };

        if (props.multiple) {
            const showTextOverflowHint = Boolean(!description && !hasErrorOrWarning);

            return (
                <Tooltip trigger={tooltipTrigger} value={errorText} pos={tooltipPosition}>
                    <TextOverflowHint showHint={showTextOverflowHint} text={value}>
                        {renderRawInput()}
                    </TextOverflowHint>
                </Tooltip>
            );
        }

        const picklistHintOpened = tooltipTrigger === "closed" && !hasErrorOrWarning;

        return (
            <Tooltip trigger={tooltipTrigger} value={errorText} pos={tooltipPosition}>
                <TreePicklistHint {...props} value={value} hintOpened={picklistHintOpened}>
                    {renderRawInput()}
                </TreePicklistHint>
            </Tooltip>
        );
    };

    const handleFocus = () => {
        const inputElement = toHtmlElementOrNull(findDOMNode(input.current));
        if (inputElement) {
            scrollTo(inputElement);
            inputElement.focus();
        }
    };

    return (
        <FocusManagementObserver onRequestFocus={handleFocus}>
            <Root group width={props.width}>
                {renderInput()}
                <GroupButtonContainer>
                    <Button
                        narrow={true}
                        data-tid={props["data-tid"]}
                        data-focus="focus"
                        corners={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                        onClick={handleOpenModal}>
                        <InternalIcon name="UiMenuDots3HIcon" size={14} />
                    </Button>
                </GroupButtonContainer>
                {modalOpened && renderModal()}
            </Root>
        </FocusManagementObserver>
    );
};
